/* Pocketknife PriceBreakQtyTracker
 * Resolves pocketknife price-break unit prices from #price-break-data-{productId} by quantity.
 * Tier values are decimal dollars per unit (including sub-$1, e.g. 0.37 → $0.37). The sole multiplier
 * case is the sentinel 1 (no tier matched / list price): Shopify base cents × 1.
 * Tier rule: largest break qty <= current qty uses that tier; if qty is below every tier, list price.
 */

/** Logs are on by default. Silence with `localStorage.setItem('pkPriceBreakDebug','0')` (reload page). */
const PK_PRICE_BREAK_DEBUG = typeof localStorage === "undefined" || localStorage.getItem("pkPriceBreakDebug") !== "0";

function pkPriceBreakLog(...args) {
  if (!PK_PRICE_BREAK_DEBUG) return;
  console.log("[PriceBreakQtyTracker]", ...args);
}

class PriceBreakQtyTracker extends HTMLElement {
  constructor() {
    super();
    this._abort = new AbortController();
    /** @type {{ signal: AbortSignal }} */
    this._listenerOpts = { signal: this._abort.signal };
  }

  disconnectedCallback() {
    this._abort.abort();
    this._abort = new AbortController();
    this._listenerOpts = { signal: this._abort.signal };
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId || !sectionId) {
      pkPriceBreakLog("init skipped: missing data-product-id or data-section-id", {
        productId,
        sectionId,
      });
      return;
    }

    pkPriceBreakLog("init", { productId, sectionId });

    const qtyInput = document.querySelector(`#Quantity-${sectionId}`);
    if (qtyInput) {
      pkPriceBreakLog("quantity input bound", `#Quantity-${sectionId}`);
      qtyInput.addEventListener("input", () => this.sync(), this._listenerOpts);
      qtyInput.addEventListener("change", () => this.sync(), this._listenerOpts);
    } else {
      pkPriceBreakLog("no quantity input found", `#Quantity-${sectionId}`);
    }

    document.addEventListener(
      "variant:changed",
      (ev) => {
        const productInfo = this.getProductInfoRoot(sectionId);
        if (!productInfo || productInfo.dataset.productId !== String(productId)) return;
        const fromDoc = /** @type {CustomEvent<{ variant?: unknown }>} */ (ev).detail?.variant;
        pkPriceBreakLog("variant:changed → sync", { hasDetailVariant: fromDoc != null });
        /** Same source as `<price-breaks>` + Price block: avoids stale `variant-picker` script during Minimog `viewTransition`. */
        this.sync(fromDoc);
      },
      this._listenerOpts,
    );

    document.addEventListener(
      "price-break-data:updated",
      (ev) => {
        if (/** @type {CustomEvent<{ productId?: string }>} */ (ev).detail?.productId !== String(productId)) {
          return;
        }
        pkPriceBreakLog("price-break-data:updated → sync");
        this.sync();
      },
      this._listenerOpts,
    );

    this.sync();
  }

  /**
   * Unit list price in **cents** from `#price-break-bases-{id}` (decimal dollars; updated by GET /prices merge).
   * @param {string} productId
   * @param {string} variantIdStr
   * @returns {number | null}
   */
  loadVariantBaseCents(productId, variantIdStr) {
    const el = document.getElementById(`price-break-bases-${productId}`);
    if (!el?.textContent?.trim()) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
      const dec = Number(/** @type {Record<string, unknown>} */ (parsed)[variantIdStr]);
      if (!Number.isFinite(dec) || dec <= 0) return null;
      return Math.round(dec * 100);
    } catch {
      return null;
    }
  }

  /** @returns {Record<string, Record<string, string>> | null} */
  loadBreaks(productId) {
    const id = `price-break-data-${productId}`;
    const el = document.getElementById(id);
    if (!el?.textContent?.trim()) {
      pkPriceBreakLog("loadBreaks: no script or empty", id);
      return null;
    }
    try {
      const data = JSON.parse(el.textContent);
      pkPriceBreakLog("loadBreaks: parsed", {
        scriptId: id,
        variantIds: Object.keys(data),
      });
      return data;
    } catch (e) {
      pkPriceBreakLog("loadBreaks: JSON parse failed", id, e);
      return null;
    }
  }

  /**
   * JSON from Liquid often uses keys like "30.0" while tier math uses 30 — lookup by numeric equality.
   * @param {Record<string, unknown>} breaks
   * @param {number} chosenTierQty
   */
  getBreakValueForTier(breaks, chosenTierQty) {
    for (const [k, v] of Object.entries(breaks)) {
      const n = Number(k);
      if (!Number.isFinite(n) || n <= 0) continue;
      if (n === chosenTierQty) return v;
    }
    const legacy = breaks[String(chosenTierQty)];
    return legacy !== undefined ? legacy : breaks[chosenTierQty];
  }

  /**
   * @param {Record<string, unknown> | undefined} breaks
   * @returns {string | null} Tier value string, `"1"` when there are no qty tiers (use Shopify `variant.price`).
   */
  resolveMultiplierString(breaks, qty) {
    if (breaks == null || typeof breaks !== "object" || Array.isArray(breaks)) {
      pkPriceBreakLog("resolveMultiplier", { qty, reason: "no_break_map_use_base_multiplier_1" });
      return "1";
    }
    const numericKeys = Object.keys(breaks)
      .map((k) => Number(k))
      .filter((k) => Number.isFinite(k) && k > 0)
      .sort((a, b) => a - b);
    if (numericKeys.length === 0) {
      pkPriceBreakLog("resolveMultiplier", {
        qty,
        reason: "empty_tiers_use_base_multiplier_1",
      });
      return "1";
    }

    const atOrBelow = numericKeys.filter((k) => k <= qty);
    if (atOrBelow.length === 0) {
      pkPriceBreakLog("resolveMultiplier", {
        qty,
        numericTiers: numericKeys,
        atOrBelowTiers: atOrBelow,
        chosenTierQty: null,
        multiplierRaw: "1",
        reason: "below_all_tiers_use_base_price",
      });
      return "1";
    }
    const chosenKey = Math.max(...atOrBelow);
    const raw = this.getBreakValueForTier(breaks, chosenKey);
    pkPriceBreakLog("resolveMultiplier", {
      qty,
      numericTiers: numericKeys,
      atOrBelowTiers: atOrBelow,
      chosenTierQty: chosenKey,
      multiplierRaw: raw,
    });
    if (raw === null || raw === undefined || raw === "") return null;
    return String(raw);
  }

  /**
   * Main PDP sets `id="MainProduct-{sectionId}"` on `<product-info>`; quick-view does not.
   * Prefer the `<product-info>` ancestor of this element, then fall back to id / data-section-id.
   * @param {string} sectionId
   * @returns {HTMLElement | null}
   */
  getProductInfoRoot(sectionId) {
    const selfRoot = this.closest("product-info");
    if (selfRoot && selfRoot.dataset.sectionId === String(sectionId)) {
      return selfRoot;
    }
    return document.getElementById(`MainProduct-${sectionId}`) || document.querySelector(`product-info[data-section-id="${CSS.escape(sectionId)}"]`);
  }

  /**
   * @param {unknown} variant
   * @param {Record<string, unknown> | null} breaks
   */
  isVariantInBreakData(variant, breaks) {
    if (!breaks || !variant || typeof variant !== "object" || variant === null) return false;
    const id = /** @type {{ id?: unknown }} */ (variant).id;
    if (id == null) return false;
    return String(id) in breaks;
  }

  /**
   * Prefer a visible `<variant-picker>`: `HTMLUpdateUtility.viewTransition` keeps the old picker in DOM with `display:none`.
   * Match `<price-breaks>` / product-info and only use `script[data-selected-variant]` (JSON), not form inputs.
   * @param {string} sectionId
   */
  getSelectedVariant(sectionId) {
    const productInfo = this.getProductInfoRoot(sectionId);
    if (!productInfo) return null;
    const pickers = productInfo.querySelectorAll("variant-picker");
    for (const picker of pickers) {
      const pe = /** @type {HTMLElement} */ (picker);
      if (pe.style.display === "none") continue;
      const el = picker.querySelector("script[data-selected-variant]");
      const raw = el?.textContent?.trim();
      if (!raw) continue;
      try {
        return JSON.parse(raw);
      } catch {
        continue;
      }
    }
    const fallback = productInfo.querySelector("variant-picker script[data-selected-variant]");
    const raw = fallback?.textContent?.trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * Tier value from JSON is a decimal **unit price** in shop money (same as `pricebreaks.effectiveBreakUnitDecimal`).
   * The only non-absolute case is **1**: treated as “list price” (multiply base cents by 1) when no tier applies.
   * @param {number} variantPriceCents
   */
  resolveUnitPriceCents(variantPriceCents, breakValueNum) {
    const m = breakValueNum;
    const base = Number(variantPriceCents);
    if (!Number.isFinite(base) || base <= 0) return 0;
    if (!Number.isFinite(m) || m <= 0) return Math.round(base);
    if (m === 1) return Math.round(base);
    return Math.round(m * 100);
  }

  formatMoney(cents) {
    if (typeof formatMoney === "function" && window.MinimogSettings?.money_format) {
      return formatMoney(cents, window.MinimogSettings.money_format);
    }
    return (cents / 100).toFixed(2);
  }

  /**
   * @param {unknown} [variantFromEvent] `variant:changed` detail.variant — aligns with fetched section HTML.
   */
  sync(variantFromEvent) {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId || !sectionId) return;

    const allBreaks = this.loadBreaks(productId);
    const qtyInput = document.querySelector(`#Quantity-${sectionId}`);

    if (!allBreaks) {
      pkPriceBreakLog("sync abort: no breaks JSON");
      return;
    }

    let variant = null;
    let variantSource = "dom";
    if (this.isVariantInBreakData(variantFromEvent, allBreaks)) {
      variant = variantFromEvent;
      variantSource = "event";
    }
    if (!variant) {
      variant = this.getSelectedVariant(sectionId);
    }

    if (!variant || variant.id == null) {
      pkPriceBreakLog("sync abort: no variant from event or variant-picker script", {
        sectionId,
      });
      return;
    }

    const breaksForVariant = /** @type {Record<string, unknown>} */ (allBreaks[String(variant.id)] ?? {});
    const qtyRaw = qtyInput ? Number.parseInt(String(qtyInput.value), 10) : 1;
    const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
    pkPriceBreakLog("sync", {
      variantId: variant.id,
      variantSource,
      qtyInputRaw: qtyInput?.value,
      qtyEffective: qty,
      breaksForVariant: Object.keys(breaksForVariant).length > 0 ? breaksForVariant : "(no qty tiers — list price)",
    });

    let multStr = this.resolveMultiplierString(breaksForVariant, qty);
    if (multStr == null) {
      multStr = "1";
      pkPriceBreakLog("sync: fallback multiplier 1 (unexpected null from tier resolution)");
    }

    const breakNum = parseFloat(multStr);
    const shopifyVariantCents = Math.round(Number(variant.price));
    const baseCentsFromBases = this.loadVariantBaseCents(productId, String(variant.id)) ?? shopifyVariantCents;
    const priceCents = this.resolveUnitPriceCents(baseCentsFromBases, breakNum);
    const isAbsoluteDollar =
      Number.isFinite(breakNum) && breakNum > 0 && breakNum !== 1;

    /** Minimog only swaps to the sale column when Shopify marks compare_at > price (see product-prices.liquid). */
    const shopifyOnSale = Number(variant.compare_at_price) > Number(variant.price);

    const compareAtCents = variant.compare_at_price ? (isAbsoluteDollar ? Math.round(Number(variant.compare_at_price)) : Math.round(Number(variant.compare_at_price) * breakNum)) : null;

    /** Quick-view uses `.m-product-quickview--info`; PDP uses `.m-main-product--info`. */
    const priceColumn = this.closest(".m-main-product--info") || this.closest(".m-product-quickview--info");
    let mainRegular = priceColumn?.querySelector(".m-price__regular .m-price-item--regular");
    let mainSale = priceColumn?.querySelector(".m-price__sale .m-price-item--sale.m-price-item--last");

    // product-prices.liquid may render a bare span.m-price-item--regular
    // without the .m-price__regular wrapper div (the `{% if true %}` path).
    if (!mainRegular && !mainSale) {
      mainRegular = priceColumn?.querySelector(".m-price .m-price-item--regular");
    }

    const unitHtml = `${this.formatMoney(priceCents)}`;

    if (mainRegular) mainRegular.innerHTML = unitHtml;
    if (mainSale) mainSale.innerHTML = unitHtml;

    if (shopifyOnSale) {
      const compareRow = priceColumn?.querySelector(".m-price__sale s.m-price-item--regular");
      if (compareRow && compareAtCents != null) {
        compareRow.innerHTML = `${this.formatMoney(compareAtCents)}`;
      }
    }

    if (mainRegular || mainSale) {
      pkPriceBreakLog("dom updated", {
        breakValue: breakNum,
        unitPriceInterpretation: isAbsoluteDollar ? "dollars_per_unit" : "multiplier",
        shopifyOnSale,
        variantPriceCents: variant.price,
        baseCentsFromBases,
        priceCents,
        compareAtCents,
        unitHtml,
      });
    } else {
      pkPriceBreakLog("sync: no price node to update (missing .m-price__regular / .m-price__sale)");
    }
  }
}

customElements.define("pricebreak-qty-tracker", PriceBreakQtyTracker);
